/**
 * Normalisation import CKA — accents, téléphones, courriels, dates Excel, ceintures.
 */
import { INSTRUCTOR_KEYS, TO_COMPLETE } from "./ckaImportConstants.js";

export function foldName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[-'’.]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function splitFullName(fullName) {
  const cleaned = String(fullName || "").replace(/\s+/g, " ").trim();
  if (!cleaned) return { firstName: "", lastName: "" };
  const parts = cleaned.split(" ");
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts.slice(0, -1).join(" "), lastName: parts[parts.length - 1] };
}

export function normalizeEmail(value) {
  const email = String(value || "").trim().toLowerCase();
  if (!email) return { email: "", suspect: false };
  const suspect = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.includes(",") || email.includes(" ");
  return { email, suspect };
}

export function normalizePhone(value) {
  const raw = String(value || "").trim();
  const digits = raw.replace(/\D/g, "");
  let display = raw;
  if (digits.length === 11 && digits.startsWith("1")) {
    display = `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 10) {
    display = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return { raw, display: display || "", digits };
}

const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);

export function excelSerialToIsoDate(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    return value.trim().slice(0, 10);
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n < 20000 || n > 60000) return "";
  const ms = Math.round((n - Math.floor(n) === 0 ? n : Math.floor(n)) * 86400000);
  const dt = new Date(EXCEL_EPOCH_UTC + ms);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
}

export function ageFromBirthDate(iso, onDate = "2026-09-01") {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const [oy, om, od] = onDate.split("-").map(Number);
  let age = oy - y;
  if (om < m || (om === m && od < d)) age -= 1;
  return age;
}

export function matchInstructorKey(rawName) {
  const f = foldName(rawName);
  if (!f) return { key: null, flags: ["instructor-missing"] };
  const flags = [];
  if (f.includes("yannick") && f.includes("kammer")) {
    return { key: INSTRUCTOR_KEYS.YANNICK, flags };
  }
  if (f.includes("gilles") && f.includes("lavigne")) {
    if (f.includes("jaki") || f.includes("jacki") || f.includes("arnaud")) {
      flags.push("dual-instructor-gilles-jaki");
    }
    return { key: INSTRUCTOR_KEYS.GILLES, flags };
  }
  if (f.includes("lachapelle") && /steph/.test(f)) {
    if (f.includes("stephanie") || f.includes("stephanne")) flags.push("instructor-name-variant");
    return { key: INSTRUCTOR_KEYS.STEPHANE, flags };
  }
  if ((f.includes("jaki") || f.includes("jacki") || f.includes("jackie")) && f.includes("arnaud")) {
    return { key: INSTRUCTOR_KEYS.JAKI, flags };
  }
  return { key: null, flags: ["instructor-unmatched"] };
}

export function matchCanonicalDojo(rawDojo) {
  const f = foldName(rawDojo);
  if (!f) return { key: null, flags: ["dojo-missing"] };
  if (f.includes("gracefield")) return { key: "gracefield", flags: [] };
  if (f.includes("vdm") || (f.includes("val des monts") && f.includes("shotokan"))) {
    return { key: "vdm", flags: [] };
  }
  if (f.includes("international") || (f.includes("aamc") && f.includes("gatineau"))) {
    return { key: "aamc-gatineau", flags: [] };
  }
  if (f.includes("shotokan gatineau") && !f.includes("international") && !f.includes("aamc")) {
    return { key: "shotokan-gatineau", flags: [] };
  }
  if (f.includes("lachapelle") || f.includes("stephane") || f.includes("stephane")) {
    return { key: "stephane-lachapelle", flags: [] };
  }
  return { key: null, flags: ["dojo-unmapped"] };
}

const BELT_RULES = [
  { pattern: /\bkudan\b|9(e|th)?\s*dan/, belt: "noire", grade: "Kudan" },
  { pattern: /\bhachidan\b|8(e|th)?\s*dan/, belt: "noire", grade: "Hachidan" },
  { pattern: /\bnanadan\b|7(e|th)?\s*dan/, belt: "noire", grade: "Nanadan" },
  { pattern: /\brokudan\b|6(e|th)?\s*dan/, belt: "noire", grade: "Rokudan" },
  { pattern: /\bgodan\b|5(e|th)?\s*dan/, belt: "noire", grade: "Godan" },
  { pattern: /\byondan\b|4(e|th)?\s*dan|noir\w*\s*4/, belt: "noire", grade: "Yondan" },
  { pattern: /\bsandan\b|3(e|rd|e)?\s*dan|3e dan/, belt: "noire", grade: "Sandan" },
  { pattern: /\bnidan\b|2(e|nd)?\s*dan|2e dan|2nd degree|black belt 2/, belt: "noire", grade: "Nidan" },
  { pattern: /\bshodan\b|1(e|st|er)?\s*dan|1e dan|noir\s*1/, belt: "noire", grade: "Shodan" },
  { pattern: /\bblack\b|\bnoire?\b|\bnoir\b/, belt: "noire", grade: "noire" },
  { pattern: /\bbrown\b|\bbrun\b|1(st|er|e)?\s*kyu/, belt: "brune", grade: "1er kyu" },
  { pattern: /\bblue\b|\bbleu\b|2(nd|e)?\s*kyu/, belt: "bleue", grade: "2e kyu" },
  { pattern: /\bgreen\b|\bvert\b|3(rd|e)?\s*kyu/, belt: "verte", grade: "3e kyu" },
  { pattern: /\borange\b|4(th|e)?\s*kyu/, belt: "orange", grade: "4e kyu" },
  { pattern: /\byellow\b|\bjaune\b/, belt: "jaune", grade: "jaune" },
  { pattern: /\bwhite\b|\bblanche\b/, belt: "blanche", grade: "blanche" },
];

export function normalizeBelt(raw) {
  const source = String(raw || "").trim();
  if (!source) return { belt: TO_COMPLETE, grade: TO_COMPLETE, raw: "", mapped: false };
  const f = foldName(source);
  for (const rule of BELT_RULES) {
    if (rule.pattern.test(f)) {
      return { belt: rule.belt, grade: rule.grade, raw: source, mapped: true };
    }
  }
  return { belt: source, grade: source, raw: source, mapped: false, flag: "belt-unmapped" };
}

export function isNotApplicable(value) {
  const f = foldName(value);
  return !f || f.includes("not applicable") || f.includes("sans objet") || f === "n a" || f === "na";
}

export function medicalHasCondition(medical, other) {
  const med = String(medical || "").trim();
  const oth = String(other || "").trim();
  if (isNotApplicable(med) && (!oth || isNotApplicable(oth) || /^non$/i.test(oth))) return false;
  return Boolean(med || oth);
}
